import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  InputGroup,
  useToast,
  Text,
  Switch,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ImageUpload } from '@/components/ImageUpload';
import { PostClubRequest } from '@/services/request/club';
import { usePostClubMutation } from '@/services/queries/club';
import { useTranslation } from 'react-i18next';
import { Club } from '@/model';

export interface TeamsCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
  itemsData: Club[]; // Adicionado
}

export const TeamsCreateModal = ({ isOpen, onClose, onDone, itemsData }: TeamsCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const postItemMutation = usePostClubMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<ModalFormData>({
    defaultValues: {
      name: '',
      file: undefined,
      acronym: '',
      is_favorite: false,
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      if (data.is_favorite && itemsData.some(club => club.favorite)) {
        toast({
          status: 'warning',
          title: t('pages.application_teams.components.teams_create_modal.teams_create_favorite_exists'),
        });
        return;
      }

      const payload: PostClubRequest = {
        image: data.file,
        name: data.name,
        acronym: data.acronym,
        is_favorite: data.is_favorite,
      };

      postItemMutation.mutate(payload, {
        onSuccess: () => {
          onDone?.();
          reset();
          toast({
            status: 'success',
            title: t('pages.application_teams.components.teams_create_modal.teams_create_success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.application_teams.components.teams_create_modal.teams_create_error'),
          });
        },
      });
    },
    [onDone, postItemMutation, reset, toast, t, itemsData],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_teams.components.teams_create_modal.teams_create_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <Flex direction='row' gap={4} width='100%'>
              <InputField
                withoutFloating
                label={t('pages.application_teams.components.teams_create_modal.teams_create_name_label')}
                {...register('name')}
                helperText={t('pages.application_teams.components.teams_create_modal.teams_create_name_helper_text')}
                maxLength={100}
                rootProps={{ isInvalid: !!errors?.name, errorMessage: errors?.name?.message }}
                flex='1'
              />

              <InputField
                withoutFloating
                label={t('pages.application_teams.components.teams_create_modal.teams_create_acronym_label')}
                {...register('acronym')}
                helperText={t('pages.application_teams.components.teams_create_modal.teams_create_acronym_helper_text')}
                maxLength={3}
                rootProps={{ isInvalid: !!errors?.acronym, errorMessage: errors?.acronym?.message }}
                flex='1'
              />
            </Flex>

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <ImageUpload
                label={t('pages.application_teams.components.teams_create_modal.teams_create_shield_label')}
                imageURL={''}
                onFileChange={file => setValue('file', file)}
                helpText={t('pages.application_teams.components.teams_create_modal.teams_create_shield_helper_text')}
                error={errors?.file?.message?.toString()}
              />
            </InputGroup>
            <Flex direction='column' gap={1}>
              <Flex alignItems='center' gap={2}>
                <Switch {...register('is_favorite')} />
                <Text>{t('pages.application_teams.components.teams_create_modal.teams_create_favorite_label')}</Text>
              </Flex>
              <Text fontSize='sm' color='gray.500'>
                {t('pages.application_teams.components.teams_create_modal.teams_create_favorite_description')}
              </Text>
            </Flex>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w='100%' justifyContent={'space-between'} alignItems='center' gap={2} paddingLeft={6}>
            <Flex w={'100%'} justifyContent='end' gap={5}>
              <Button variant='outlined' color='gray.600' onClick={onClose}>
                {t('pages.application_teams.components.teams_create_modal.teams_create_cancel')}
              </Button>
              <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
                {t('pages.application_teams.components.teams_create_modal.teams_create_submit')}
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={postItemMutation.isLoading} />
    </Modal>
  );
};
